import {Injectable, Optional} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {ConfigService} from '@ngmedax/config';
import {LayoutService} from '@ngmedax/layout';
import {LoginEventService} from '@ngmedax/login';
import {ThemeService} from '@ngmedax/theme';
import {AdminTheming, UiSettings} from '../../../types';
import {SettingsThemeService} from './settings-theme.service';
import {adminPalette} from './admin-palette';

/** Settings keys of the instance (mongodb settings collection) */
export const UI_SETTINGS_KEYS = {
  theme: 'adminUiTheme',
  editor: 'adminUiEditor',
  submenu: 'adminUiSubmenu'
};

export const ADMIN_THEMING_KEYS = {
  active: 'adminThemingActive',
  mainColor: 'adminThemingMainColor',
  font: 'adminThemingFont',
  logo: 'adminThemingLogo',
  logoAsset: 'adminThemingLogoAsset',
  customCss: 'adminThemingCustomCss'
};

/** Fonts of the brand, the same as in the patient app. The files come with the theme (fonts.css next to theme.css) */
export const ADMIN_FONTS = ['Asap', 'Open Sans', 'Caveat', 'Exo', 'Roboto Condensed'];

const ALLOWED = {theme: ['new', 'classic'], editor: ['new', 'classic'], submenu: ['top', 'left']};
const CACHE_KEY = 'mm-admin-ui-settings';
const EMPTY: UiSettings = {theme: null, editor: null, submenu: null, adminTheming: null};

/**
 * Look of the administration for the whole instance: base theme, questionnaire editor, sub menu position and the
 * brand (main color, font, logo, own css). The config (ui.theme.default, ui.editor.default, ui.submenu.default) sets
 * the defaults, settings stored in the instance win over them. The effective values are written back into the config,
 * so modules keep reading the config only.
 *
 * Everything is optional: without settings api, login, theme module or theme css the config defaults simply stay.
 */
@Injectable()
export class UiSettingsService {
  private settings: UiSettings = {...EMPTY};
  private defaults: {theme: string, editor: string, submenu: string} = null;
  private initialized = false;
  private loadedFromApi = false;
  private navigationRefreshes = 0;

  public constructor(
    private config: ConfigService,
    private layout: LayoutService,
    private api: SettingsThemeService,
    @Optional() private theme: ThemeService,
    @Optional() private loginEvents: LoginEventService,
    @Optional() private router: Router
  ) {
  }

  /**
   * Applies the cached settings as soon as the config is there, then refreshes them from the api after the login.
   * Resolves without waiting for the api, so a slow or missing api never blocks the start of the app
   */
  public init(): Promise<void> {
    if (this.initialized) {
      return Promise.resolve();
    }
    this.initialized = true;

    this.loginEvents && this.loginEvents.onAfterLogin().subscribe(() => this.refresh());

    // the theme guard sets the theme logo on the first navigation, the brand logo has to win. a restored session
    // (reload without login event) is known after the first navigation, then the settings get loaded
    this.router && this.router.events.subscribe(event => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      this.applyLogo();
      !this.loadedFromApi && this.navigationRefreshes++ < 3 && this.refresh();
    });

    return this.whenConfigLoaded().then(() => {
      this.defaults = {
        theme: this.config.get('ui.theme.default') === 'classic' ? 'classic' : 'new',
        editor: this.config.get('ui.editor.default') === 'classic' ? 'classic' : 'new',
        submenu: this.config.get('ui.submenu.default') === 'left' ? 'left' : 'top'
      };
      this.settings = this.readCache();
      this.apply();
      this.refresh();
    });
  }

  /** Defaults of the config, shown as preset in the settings */
  public getDefaults(): {theme: string, editor: string, submenu: string} {
    return this.defaults || {theme: 'new', editor: 'new', submenu: 'top'};
  }

  /** Settings stored in the instance, null values fall back to the config */
  public getSettings(): UiSettings {
    return {...this.settings};
  }

  /** Effective value of a switch: instance setting, otherwise config default */
  public getEffective(key: 'theme' | 'editor' | 'submenu'): string {
    return this.settings[key] || this.getDefaults()[key];
  }

  /** Loads the settings of the instance. Keeps the current ones when the api is not available */
  public async refresh(): Promise<void> {
    try {
      const keys = Object.keys(UI_SETTINGS_KEYS);
      const themingKeys = Object.keys(ADMIN_THEMING_KEYS);
      const read = (key: string) => this.api.read(key).catch(() => undefined);

      // the first key probes the api: no answer means no api or no login, keep what we have
      const first = await read(UI_SETTINGS_KEYS[keys[0]]);
      if (first === undefined) {
        return;
      }

      const values = [first, ...await Promise.all([
        ...keys.slice(1).map(key => read(UI_SETTINGS_KEYS[key])),
        ...themingKeys.map(key => read(ADMIN_THEMING_KEYS[key]))
      ])];

      const settings: UiSettings = {...EMPTY};
      keys.forEach((key, index) => settings[key] = this.valid(key, values[index]));

      const theming: any = {};
      themingKeys.forEach((key, index) => theming[key] = values[keys.length + index]);
      settings.adminTheming = theming.mainColor || theming.logo || theming.customCss ? {
        active: theming.active === true || theming.active === 'true',
        mainColor: theming.mainColor || '',
        font: theming.font || '',
        logo: theming.logo || null,
        logoAsset: theming.logoAsset || null,
        customCss: theming.customCss || ''
      } : null;

      this.loadedFromApi = true;
      this.settings = settings;
      this.writeCache();
      this.apply();
    } catch (error) {
      console.warn('settings theming: unable to load the ui settings', error);
    }
  }

  /** Stores the switches of the instance. null removes a value, so the config default applies again */
  public async saveSwitches(values: {theme?: string | null, editor?: string | null, submenu?: string | null}): Promise<void> {
    const data: any = {};
    for (const key of Object.keys(values)) {
      data[UI_SETTINGS_KEYS[key]] = this.valid(key, values[key]) || '';
    }
    await this.api.save(data);
    Object.keys(values).forEach(key => this.settings[key] = this.valid(key, values[key]));
    this.writeCache();
    this.apply();
  }

  /** Stores the brand of the administration */
  public async saveAdminTheming(theming: AdminTheming): Promise<void> {
    const data: any = {};
    Object.keys(ADMIN_THEMING_KEYS).forEach(key => data[ADMIN_THEMING_KEYS[key]] = theming[key] === null ? '' : theming[key]);
    await this.api.save(data);
    this.settings.adminTheming = {...theming};
    this.writeCache();
    this.applyAdminTheming(this.settings.adminTheming);
  }

  /** Shows a brand without storing it (preview of the settings page), null shows the stored one again */
  public preview(theming: AdminTheming | null): void {
    this.applyAdminTheming(theming || this.settings.adminTheming);
  }

  /** Applies all settings: writes the effective values into the config and updates theme, menu and brand */
  private apply(): void {
    const theme = this.getEffective('theme');
    const editor = this.getEffective('editor');
    const submenu = this.getEffective('submenu');

    this.safely(() => this.config.modify((config: any) => {
      config.ui = config.ui || {};
      config.ui.theme = {...(config.ui.theme || {}), default: theme};
      config.ui.editor = {...(config.ui.editor || {}), default: editor};
      config.ui.submenu = {...(config.ui.submenu || {}), default: submenu};
    }));

    this.safely(() => this.theme && this.theme.setDefaultBaseTheme(theme));
    this.safely(() => this.layout.setClassicNavigation(submenu === 'left'));
    this.applyAdminTheming(this.settings.adminTheming);
  }

  /** Overrides the brand tokens of the theme, loads the font and own css, or removes all of it */
  private applyAdminTheming(theming: AdminTheming | null): void {
    this.safely(() => {
      const active = !!(theming && theming.active);
      const palette = active ? adminPalette(theming.mainColor) : null;
      const font = active && ADMIN_FONTS.indexOf(theming.font) !== -1 ? theming.font : '';

      document.documentElement.classList.toggle('mm-admin-ci', active);
      this.setStyle('mm-admin-ci-tokens', active ? this.tokenCss(palette, font) : '');
      this.setStyle('mm-admin-ci-custom', active ? theming.customCss || '' : '');
      font && this.loadFonts();
      this.applyLogo(theming);
    });
  }

  /**
   * Css with the token overrides. Like the theme, light tokens go to the root element (tokens referencing tokens are
   * resolved there) and dark tokens to body.dark-theme, each one class more specific than the theme blocks
   */
  private tokenCss(palette: {light: any, dark: any} | null, font: string): string {
    const block = (tokens: any) => Object.keys(tokens).map(token => `  ${token}: ${tokens[token]};`).join('\n');
    const fontStack = `"${font}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif`;
    const fontToken = font ? {'--mm-font-family': fontStack} : {};
    const light = {...(palette ? palette.light : {}), ...fontToken};
    const dark = {...(palette ? palette.dark : {}), ...fontToken};

    return `html.mm-admin-ci {\n${block(light)}\n}\nhtml.mm-admin-ci body.dark-theme {\n${block(dark)}\n}\n`;
  }

  /** Brand logo in the side menu, otherwise the logo of the theme */
  private applyLogo(theming: AdminTheming | null = this.settings.adminTheming): void {
    this.safely(() => {
      const brandLogo = theming && theming.active && theming.logo && theming.logo.dataUrl;
      const themeLogo = (this.theme && this.theme.getLogo()) || this.config.get('logo') || null;
      const logo = brandLogo || themeLogo;
      this.layout.getPageLogo() !== logo && this.layout.setPageLogo(logo);
    });
  }

  /** Loads the font faces of the brand fonts once, from fonts.css next to the theme css */
  public loadFonts(): void {
    const cssPath = (this.theme && this.theme.getCssFilePath()) || this.config.get('css');
    if (!cssPath || document.getElementById('mm-admin-ci-fonts')) {
      return;
    }
    const link = document.createElement('link');
    link.id = 'mm-admin-ci-fonts';
    link.rel = 'stylesheet';
    link.href = cssPath.replace(/[^/]*$/, 'fonts.css');
    document.head.appendChild(link);
  }

  /** Sets the content of a style element at the end of the head, removes it for empty css */
  private setStyle(id: string, css: string): void {
    let style = document.getElementById(id);
    if (!css) {
      style && style.parentNode.removeChild(style);
      return;
    }
    if (!style) {
      style = document.createElement('style');
      style.id = id;
    }
    style.textContent = css;
    document.head.appendChild(style);
  }

  /** Accepts only known values, anything else means "not set" */
  private valid(key: string, value: any): any {
    return ALLOWED[key] && ALLOWED[key].indexOf(value) !== -1 ? value : null;
  }

  private whenConfigLoaded(): Promise<void> {
    if (this.config.isLoaded()) {
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      const timeout = setTimeout(resolve, 10000);
      this.config.onAfterLoad().subscribe(() => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }

  /** Last loaded settings of this instance, applied on start so the look does not flicker until the api answered */
  private readCache(): UiSettings {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      return cached ? {...EMPTY, ...cached} : {...EMPTY};
    } catch (error) {
      return {...EMPTY};
    }
  }

  private writeCache(): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.settings));
    } catch (error) {
      // a full or blocked storage only costs the cache
    }
  }

  /** Runs a step of applying the settings, a failing step must not break the app */
  private safely(step: () => void): void {
    try {
      step();
    } catch (error) {
      console.warn('settings theming: unable to apply a ui setting', error);
    }
  }
}
